import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { scheduleApi, customerApi } from '../api';
import {
  SCHEDULE_CATEGORIES, SCHEDULE_CATEGORY_COLORS,
  SCHEDULE_STATUSES, SCHEDULE_STATUS_COLORS,
  SCHEDULE_SECTIONS, SECTION_CATEGORY_MAP,
  CUSTOMER_STATUS_COLORS,
} from '../constants';

type SectionKey = 'store' | 'rental' | 'payment';

const SECTION_CONFIG: Record<SectionKey, { label: string; color: string }> = {
  store: { label: '매장일정', color: 'bg-green-500' },
  rental: { label: '대여일정', color: 'bg-teal-500' },
  payment: { label: '결제일정', color: 'bg-amber-500' },
};

// 대여일정 카테고리
const RENTAL_CATEGORIES = ['RENTAL', 'RETURN'];
// 결제일정 카테고리
const PAYMENT_CATEGORIES = ['PAYMENT'];

const toDateStr = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// 고객일정 이벤트 타입 색상
const CUSTOMER_EVENT_COLORS: Record<string, { bg: string; dot: string; label: string }> = {
  wedding: { bg: 'bg-rose-100 text-rose-700', dot: 'bg-rose-500', label: '본식' },
  rehearsal: { bg: 'bg-violet-100 text-violet-700', dot: 'bg-violet-500', label: '리허설' },
  contract: { bg: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500', label: '계약' },
};

export default function SuitSchedules() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'store' | 'customer'>('store');

  // === 고객일정 탭 state ===
  const [customerEvents, setCustomerEvents] = useState<any[]>([]);
  const [isCustomerLoading, setIsCustomerLoading] = useState(false);
  const [customerCalDate, setCustomerCalDate] = useState(new Date());
  const [customerSelectedDate, setCustomerSelectedDate] = useState(toDateStr(new Date()));
  const [customerEventFilter, setCustomerEventFilter] = useState<'all' | 'wedding' | 'rehearsal' | 'contract'>('all');

  const custYear = customerCalDate.getFullYear();
  const custMonth = customerCalDate.getMonth();

  const fetchCustomerEvents = useCallback(async () => {
    setIsCustomerLoading(true);
    try {
      const lastDay = new Date(custYear, custMonth + 1, 0);
      const dateFrom = `${custYear}-${String(custMonth + 1).padStart(2, '0')}-01`;
      const dateTo = `${custYear}-${String(custMonth + 1).padStart(2, '0')}-${String(lastDay.getDate()).padStart(2, '0')}`;
      const data = await customerApi.list({ dateFrom, dateTo, size: 999 });
      const customers = data.items || [];
      const events: any[] = [];
      customers.forEach((c: any) => {
        if (c.weddingDt) events.push({ type: 'wedding', date: c.weddingDt.slice(0, 10), customer: c });
        if (c.rehearsalDt) events.push({ type: 'rehearsal', date: c.rehearsalDt.slice(0, 10), customer: c });
        if (c.contractDt) events.push({ type: 'contract', date: c.contractDt.slice(0, 10), customer: c });
      });
      setCustomerEvents(events);
    } catch (err) {
      console.error('고객일정 조회 실패:', err);
    } finally {
      setIsCustomerLoading(false);
    }
  }, [custYear, custMonth]);

  useEffect(() => {
    if (activeTab === 'customer') fetchCustomerEvents();
  }, [activeTab, fetchCustomerEvents]);

  // === 매장일정 탭 state ===
  const [isCalendarLoading, setIsCalendarLoading] = useState(false);
  const [isDailyLoading, setIsDailyLoading] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // 캘린더
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarSchedules, setCalendarSchedules] = useState<any[]>([]);
  const [selectedDate, setSelectedDate] = useState(toDateStr(new Date()));

  // 일간
  const [sections, setSections] = useState<Record<SectionKey, any[]>>({
    store: [], rental: [], payment: [],
  });
  const [collapsedSections, setCollapsedSections] = useState<Record<SectionKey, boolean>>({
    store: false, rental: false, payment: false,
  });
  const [showAddModal, setShowAddModal] = useState(false);
  // 종일 카테고리: 날짜만 선택 (시간 불필요)
  const ALL_DAY_CATEGORIES = ['WEDDING', 'REHEARSAL', 'PAYMENT', 'RENTAL', 'RETURN'];

  const [form, setForm] = useState({
    section: 'STORE', scheduleCategory: 'CONSULTATION', title: '',
    startDt: '', endDt: '', isAllDay: false,
    suitCustomerIdx: '', suitContractIdx: '',
    assignedStaffIdx: '', roomIdx: '', customerName: '', memo: '',
    customerPhone: '', partnerVendorName: '', partnerStaffName: '',
  });

  // 상세 모달
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState<any>(null);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  // 월간 데이터
  const fetchCalendarSchedules = useCallback(async () => {
    setIsCalendarLoading(true);
    const lastDay = new Date(year, month + 1, 0);
    const dateFrom = `${year}-${String(month + 1).padStart(2, '0')}-01`;
    const dateTo = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDay.getDate()).padStart(2, '0')}`;
    try {
      const data = await scheduleApi.list({ dateFrom, dateTo, scheduleCategory: categoryFilter, status: statusFilter, size: 999 });
      setCalendarSchedules(data.items || []);
    } catch (err) {
      console.error('일정 조회 실패:', err);
    } finally {
      setIsCalendarLoading(false);
    }
  }, [year, month, categoryFilter, statusFilter]);

  // 일간 데이터
  const fetchDailySchedules = useCallback(async () => {
    setIsDailyLoading(true);
    try {
      const data = await scheduleApi.list({
        viewMode: 'DAILY', dateFrom: selectedDate, dateTo: selectedDate,
        scheduleCategory: categoryFilter, status: statusFilter, size: 999,
      });
      const sortByStart = (a: any, b: any) => new Date(a.startDt).getTime() - new Date(b.startDt).getTime();
      // 모든 일정을 합쳐서 카테고리 기준으로 재분류
      const all = [...(data.task || []), ...(data.rental || []), ...(data.store || []), ...(data.consultation || [])];
      setSections({
        store: all.filter((s: any) => !RENTAL_CATEGORIES.includes(s.scheduleCategory) && !PAYMENT_CATEGORIES.includes(s.scheduleCategory)).sort(sortByStart),
        rental: all.filter((s: any) => RENTAL_CATEGORIES.includes(s.scheduleCategory)).sort(sortByStart),
        payment: all.filter((s: any) => PAYMENT_CATEGORIES.includes(s.scheduleCategory)).sort(sortByStart),
      });
    } catch (err) {
      console.error('일간 일정 조회 실패:', err);
    } finally {
      setIsDailyLoading(false);
    }
  }, [selectedDate, categoryFilter, statusFilter]);

  useEffect(() => { fetchCalendarSchedules(); }, [fetchCalendarSchedules]);
  useEffect(() => { fetchDailySchedules(); }, [fetchDailySchedules]);

  // 유틸
  const getCategoryLabel = (value: string) => SCHEDULE_CATEGORIES.find(c => c.value === value)?.label || value;
  const getCategoryDotColor = (value: string) => SCHEDULE_CATEGORIES.find(c => c.value === value)?.color || 'bg-gray-400';
  const formatTime = (dt: string) => {
    if (!dt) return '';
    const d = new Date(dt);
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  };

  const handleStatusChange = async (scheduleIdx: number, newStatus: string) => {
    try {
      await scheduleApi.update(scheduleIdx, { status: newStatus });
      fetchDailySchedules();
      fetchCalendarSchedules();
    } catch (err: any) {
      alert(`상태 변경 실패: ${err.message}`);
    }
  };

  const resetForm = () => setForm({
    section: 'STORE', scheduleCategory: 'CONSULTATION', title: '',
    startDt: '', endDt: '', isAllDay: false,
    suitCustomerIdx: '', suitContractIdx: '',
    assignedStaffIdx: '', roomIdx: '', customerName: '', memo: '',
    customerPhone: '', partnerVendorName: '', partnerStaffName: '',
  });

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.section) { alert('섹션은 필수입니다.'); return; }
    if (!form.title || !form.startDt) { alert('제목과 시작 시간은 필수입니다.'); return; }
    try {
      const { isAllDay, endDt, ...formData } = form;
      await scheduleApi.create({
        ...formData,
        endDt: isAllDay ? undefined : (endDt || undefined),
        suitCustomerIdx: form.suitCustomerIdx ? Number(form.suitCustomerIdx) : undefined,
        suitContractIdx: form.suitContractIdx ? Number(form.suitContractIdx) : undefined,
        assignedStaffIdx: form.assignedStaffIdx ? Number(form.assignedStaffIdx) : undefined,
        roomIdx: form.roomIdx ? Number(form.roomIdx) : undefined,
      });
      alert('일정이 등록되었습니다.');
      setShowAddModal(false);
      resetForm();
      fetchDailySchedules();
      fetchCalendarSchedules();
    } catch (err: any) {
      alert(`등록 실패: ${err.message}`);
    }
  };

  const toggleSection = (key: SectionKey) => setCollapsedSections(prev => ({ ...prev, [key]: !prev[key] }));
  const totalCount = sections.store.length + sections.rental.length + sections.payment.length;

  // 캘린더 생성
  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));

  const generateCalendar = () => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDay = firstDay.getDay();
    const totalDays = lastDay.getDate();
    const weeks: (number | null)[][] = [];
    let week: (number | null)[] = [];
    for (let i = 0; i < startDay; i++) week.push(null);
    for (let d = 1; d <= totalDays; d++) {
      week.push(d);
      if (week.length === 7) { weeks.push(week); week = []; }
    }
    if (week.length > 0) {
      while (week.length < 7) week.push(null);
      weeks.push(week);
    }
    return weeks;
  };

  const getSchedulesForDay = (day: number) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return calendarSchedules.filter(s => s.startDt?.startsWith(dateStr));
  };

  const handleDayClick = (day: number) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    setSelectedDate(dateStr);
  };

  const weeks = generateCalendar();
  const today = new Date();
  const todayStr = toDateStr(today);
  const isToday = (day: number) => today.getFullYear() === year && today.getMonth() === month && today.getDate() === day;
  const isSelected = (day: number) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return dateStr === selectedDate;
  };

  const selectedDateObj = new Date(selectedDate + 'T00:00:00');
  const selectedDateLabel = selectedDateObj.toLocaleDateString('ko-KR', { month: 'long', day: 'numeric', weekday: 'short' });

  // 섹션별 테이블
  const renderSectionTable = (_key: SectionKey, items: any[]) => {
    return (
      <div className="divide-y divide-gray-100">
        {items.map((s: any) => (
          <div key={s.suitScheduleIdx} className="px-4 py-3 flex items-center gap-4 hover:bg-gray-50 cursor-pointer"
            onClick={() => { setSelectedSchedule(s); setShowDetailModal(true); }}>
            <span className="text-sm font-medium text-gray-800 w-20 shrink-0">{formatTime(s.startDt)}~{formatTime(s.endDt)}</span>
            <div className="flex items-center gap-1.5">
              <div className={`w-2 h-2 rounded-full ${getCategoryDotColor(s.scheduleCategory)}`}></div>
              <span className="text-xs text-gray-500">{getCategoryLabel(s.scheduleCategory)}</span>
            </div>
            <span className="text-sm flex-1 truncate text-gray-700">{s.title}</span>
            {s.room && <span className="text-xs px-1.5 py-0.5 bg-purple-50 text-purple-600 rounded shrink-0">{s.room.name}</span>}
            <span className="text-sm text-gray-500 shrink-0">{s.suitCustomer?.name || s.customerName || '-'}</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 p-8">


        {/* 헤더 */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">일정관리</h1>
            <p className="text-sm text-gray-600 mt-1">일정을 확인하고 관리합니다</p>
          </div>
          {activeTab === 'store' && (
            <button onClick={() => setShowAddModal(true)}
              className="bg-blue-600 text-white px-6 py-2.5 rounded-lg hover:bg-blue-700 flex items-center gap-2 cursor-pointer">
              <i className="ri-add-line"></i> 일정 등록
            </button>
          )}
        </div>

        {/* 탭 */}
        <div className="flex gap-1 mb-6 bg-gray-100 p-1 rounded-lg w-fit">
          <button onClick={() => setActiveTab('store')}
            className={`px-5 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer ${
              activeTab === 'store' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}>
            <i className="ri-store-2-line mr-1.5"></i>매장 일정
          </button>
          <button onClick={() => setActiveTab('customer')}
            className={`px-5 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer ${
              activeTab === 'customer' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}>
            <i className="ri-user-heart-line mr-1.5"></i>고객 일정
          </button>
        </div>

        {/* ===== 고객 일정 탭 ===== */}
        {activeTab === 'customer' && (() => {
          const custWeeks = (() => {
            const firstDay = new Date(custYear, custMonth, 1);
            const lastDay = new Date(custYear, custMonth + 1, 0);
            const startDay = firstDay.getDay();
            const totalDays = lastDay.getDate();
            const weeks: (number | null)[][] = [];
            let week: (number | null)[] = [];
            for (let i = 0; i < startDay; i++) week.push(null);
            for (let d = 1; d <= totalDays; d++) {
              week.push(d);
              if (week.length === 7) { weeks.push(week); week = []; }
            }
            if (week.length > 0) {
              while (week.length < 7) week.push(null);
              weeks.push(week);
            }
            return weeks;
          })();

          const custToday = new Date();
          const isCustToday = (day: number) => custToday.getFullYear() === custYear && custToday.getMonth() === custMonth && custToday.getDate() === day;
          const isCustSelected = (day: number) => {
            const ds = `${custYear}-${String(custMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            return ds === customerSelectedDate;
          };

          const getEventsForDay = (day: number) => {
            const ds = `${custYear}-${String(custMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            return customerEvents.filter(e => e.date === ds && (customerEventFilter === 'all' || e.type === customerEventFilter));
          };

          const selectedEvents = customerEvents
            .filter(e => e.date === customerSelectedDate && (customerEventFilter === 'all' || e.type === customerEventFilter))
            .sort((a, b) => a.type.localeCompare(b.type));

          const selectedLabel = new Date(customerSelectedDate + 'T00:00:00').toLocaleDateString('ko-KR', { month: 'long', day: 'numeric', weekday: 'short' });

          return (
            <>
              {/* 고객일정 안내 + 필터 */}
              <div className="bg-blue-50 border border-blue-100 rounded-lg px-4 py-2.5 mb-4 flex items-center gap-2 text-xs text-blue-700">
                <i className="ri-information-line text-sm"></i>
                고객의 본식, 리허설, 계약 일정이 자동으로 표시됩니다.
              </div>
              <div className="bg-white p-3 rounded-lg shadow-sm mb-6 flex items-center gap-4 flex-wrap">
                <div className="flex items-center gap-2">
                  <label className="text-xs font-medium text-gray-500">일정 유형</label>
                  <select value={customerEventFilter} onChange={(e) => setCustomerEventFilter(e.target.value as any)}
                    className="px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="all">전체</option>
                    <option value="wedding">본식</option>
                    <option value="rehearsal">리허설</option>
                    <option value="contract">계약</option>
                  </select>
                </div>
                <div className="flex items-center gap-3 ml-auto">
                  {Object.entries(CUSTOMER_EVENT_COLORS).map(([key, val]) => (
                    <div key={key} className="flex items-center gap-1">
                      <div className={`w-2.5 h-2.5 rounded-full ${val.dot}`}></div>
                      <span className="text-xs text-gray-600">{val.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-12 gap-6" style={{ height: 'calc(100vh - 240px)' }}>
                {/* 캘린더 */}
                <div className="col-span-8 overflow-y-auto">
                  <div className="bg-white rounded-lg shadow-sm overflow-hidden sticky top-8">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                      <button onClick={() => setCustomerCalDate(new Date(custYear, custMonth - 1, 1))} className="p-1.5 hover:bg-gray-100 rounded cursor-pointer">
                        <i className="ri-arrow-left-s-line text-lg text-gray-600"></i>
                      </button>
                      <h2 className="text-sm font-bold text-gray-800">{custYear}년 {custMonth + 1}월</h2>
                      <button onClick={() => setCustomerCalDate(new Date(custYear, custMonth + 1, 1))} className="p-1.5 hover:bg-gray-100 rounded cursor-pointer">
                        <i className="ri-arrow-right-s-line text-lg text-gray-600"></i>
                      </button>
                    </div>
                    {isCustomerLoading ? (
                      <div className="flex justify-center p-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>
                    ) : (
                      <div>
                        <div className="grid grid-cols-7 border-b border-gray-100">
                          {['일', '월', '화', '수', '목', '금', '토'].map((day, i) => (
                            <div key={day} className={`py-2 text-center text-[11px] font-semibold ${i === 0 ? 'text-red-400' : i === 6 ? 'text-blue-400' : 'text-gray-400'}`}>{day}</div>
                          ))}
                        </div>
                        {custWeeks.map((week, wi) => (
                          <div key={wi} className="grid grid-cols-7">
                            {week.map((day, di) => {
                              if (day === null) return <div key={di} className="min-h-32 bg-gray-50/30"></div>;
                              const dayEvents = getEventsForDay(day);
                              const selected = isCustSelected(day);
                              return (
                                <div key={di}
                                  onClick={() => setCustomerSelectedDate(`${custYear}-${String(custMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`)}
                                  className={`min-h-32 p-1 border-b border-r border-gray-50 cursor-pointer transition-colors ${
                                    selected ? 'bg-blue-50 ring-1 ring-inset ring-blue-200' : 'hover:bg-gray-50'
                                  }`}>
                                  <div className={`text-[11px] font-medium mb-0.5 w-5 h-5 flex items-center justify-center rounded-full ${
                                    isCustToday(day) ? 'bg-blue-600 text-white' :
                                    selected ? 'text-blue-700 font-bold' :
                                    di === 0 ? 'text-red-400' : di === 6 ? 'text-blue-400' : 'text-gray-600'
                                  }`}>{day}</div>
                                  <div className="space-y-px">
                                    {dayEvents.slice(0, 3).map((ev: any, ei: number) => {
                                      const cfg = CUSTOMER_EVENT_COLORS[ev.type];
                                      return (
                                        <div key={ei} className={`text-[11px] leading-snug px-1 py-0.5 rounded truncate ${cfg.bg}`}>
                                          {cfg.label} {ev.customer.name}
                                        </div>
                                      );
                                    })}
                                    {dayEvents.length > 3 && <div className="text-[11px] text-gray-400 px-1">+{dayEvents.length - 3}</div>}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* 선택 날짜 상세 */}
                <div className="col-span-4 overflow-y-auto">
                  <div className="flex items-center gap-3 mb-4">
                    <h2 className="text-lg font-bold text-gray-800">{selectedLabel}</h2>
                    <span className="text-sm text-gray-500">{selectedEvents.length}건</span>
                  </div>
                  {selectedEvents.length === 0 ? (
                    <div className="bg-white rounded-lg shadow-sm p-12 text-center text-gray-400">
                      <i className="ri-calendar-check-line text-4xl block mb-2"></i>
                      이 날의 고객 일정이 없습니다.
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {selectedEvents.map((ev: any, i: number) => {
                        const cfg = CUSTOMER_EVENT_COLORS[ev.type];
                        const c = ev.customer;
                        const statusColor = CUSTOMER_STATUS_COLORS[c.status] || 'bg-gray-100 text-gray-700';
                        return (
                          <div key={i} className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
                            onClick={() => navigate(`/suit-shop/customer-detail?id=${c.suitCustomerIdx}`)}>
                            <div className="px-4 py-3 flex items-center gap-3">
                              <div className={`w-3 h-3 rounded-full flex-shrink-0 ${cfg.dot}`}></div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-0.5">
                                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${cfg.bg}`}>{cfg.label}</span>
                                  <span className="text-sm font-semibold text-gray-900">{c.name}</span>
                                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${statusColor}`}>{c.status}</span>
                                </div>
                                <div className="flex items-center gap-3 text-xs text-gray-500">
                                  <span><i className="ri-phone-line mr-0.5"></i>{c.phone}</span>
                                  {c.weddingDt && <span>본식 {c.weddingDt.slice(5, 10)}</span>}
                                  {c.rehearsalDt && <span>리허설 {c.rehearsalDt.slice(5, 10)}</span>}
                                </div>
                              </div>
                              <i className="ri-arrow-right-s-line text-gray-300 flex-shrink-0"></i>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </>
          );
        })()}

        {/* ===== 매장 일정 탭 ===== */}
        {activeTab === 'store' && (
          <>
        {/* 필터 */}
        <div className="bg-white p-3 rounded-lg shadow-sm mb-6 flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-gray-500">카테고리</label>
            <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="">전체</option>
              {SCHEDULE_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
        </div>

        {/* 캘린더 + 일간 레이아웃 */}
        <div className="grid grid-cols-12 gap-6" style={{ height: 'calc(100vh - 180px)' }}>
          {/* 좌: 월간 캘린더 */}
          <div className="col-span-8 overflow-y-auto">
            <div className="bg-white rounded-lg shadow-sm overflow-hidden sticky top-8">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                <button onClick={prevMonth} className="p-1.5 hover:bg-gray-100 rounded cursor-pointer">
                  <i className="ri-arrow-left-s-line text-lg text-gray-600"></i>
                </button>
                <h2 className="text-sm font-bold text-gray-800">{year}년 {month + 1}월</h2>
                <button onClick={nextMonth} className="p-1.5 hover:bg-gray-100 rounded cursor-pointer">
                  <i className="ri-arrow-right-s-line text-lg text-gray-600"></i>
                </button>
              </div>

              {isCalendarLoading ? (
                <div className="flex justify-center p-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>
              ) : (
                <div>
                  <div className="grid grid-cols-7 border-b border-gray-100">
                    {['일', '월', '화', '수', '목', '금', '토'].map((day, i) => (
                      <div key={day} className={`py-2 text-center text-[11px] font-semibold ${i === 0 ? 'text-red-400' : i === 6 ? 'text-blue-400' : 'text-gray-400'}`}>
                        {day}
                      </div>
                    ))}
                  </div>
                  {weeks.map((week, wi) => (
                    <div key={wi} className="grid grid-cols-7">
                      {week.map((day, di) => {
                        if (day === null) return <div key={di} className="min-h-32 bg-gray-50/30"></div>;
                        const daySchedules = getSchedulesForDay(day);
                        const selected = isSelected(day);
                        return (
                          <div key={di} onClick={() => handleDayClick(day)}
                            className={`min-h-32 p-1 border-b border-r border-gray-50 cursor-pointer transition-colors ${
                              selected ? 'bg-blue-50 ring-1 ring-inset ring-blue-200' : 'hover:bg-gray-50'
                            }`}>
                            <div className={`text-[11px] font-medium mb-0.5 w-5 h-5 flex items-center justify-center rounded-full ${
                              isToday(day) ? 'bg-blue-600 text-white' :
                              selected ? 'text-blue-700 font-bold' :
                              di === 0 ? 'text-red-400' : di === 6 ? 'text-blue-400' : 'text-gray-600'
                            }`}>
                              {day}
                            </div>
                            <div className="space-y-px">
                              {daySchedules.slice(0, 2).map((s: any) => (
                                <div key={s.suitScheduleIdx}
                                  onClick={(e) => { e.stopPropagation(); setSelectedSchedule(s); setShowDetailModal(true); }}
                                  className={`text-[11px] leading-snug px-1 py-0.5 rounded truncate ${SCHEDULE_CATEGORY_COLORS[s.scheduleCategory] || 'bg-gray-100 text-gray-600'}`}>
                                  {s.title}
                                </div>
                              ))}
                              {daySchedules.length > 2 && (
                                <div className="text-[11px] text-gray-400 px-1">+{daySchedules.length - 2}</div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* 우: 선택된 날짜의 일간 일정 */}
          <div className="col-span-4 overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <h2 className="text-lg font-bold text-gray-800">{selectedDateLabel}</h2>
                {selectedDate === todayStr && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-medium">오늘</span>
                )}
                <span className="text-sm text-gray-500">{totalCount}건</span>
              </div>
            </div>

            {isDailyLoading ? (
              <div className="flex justify-center p-12"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div></div>
            ) : totalCount === 0 ? (
              <div className="bg-white rounded-lg shadow-sm p-12 text-center text-gray-400">
                <i className="ri-calendar-check-line text-4xl block mb-2"></i>
                이 날의 일정이 없습니다.
              </div>
            ) : (
              <div className="space-y-3">
                {(Object.keys(SECTION_CONFIG) as SectionKey[]).map(key => {
                  const items = sections[key];
                  if (items.length === 0) return null;
                  const config = SECTION_CONFIG[key];
                  const isCollapsed = collapsedSections[key];
                  return (
                    <div key={key} className="bg-white rounded-lg shadow-sm overflow-hidden">
                      <button onClick={() => toggleSection(key)}
                        className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 cursor-pointer">
                        <div className="flex items-center gap-2.5">
                          <div className={`w-2.5 h-2.5 rounded-full ${config.color}`}></div>
                          <span className="text-sm font-semibold text-gray-800">{config.label}</span>
                          <span className="text-xs text-gray-400 font-medium">{items.length}</span>
                        </div>
                        <i className={`ri-arrow-${isCollapsed ? 'down' : 'up'}-s-line text-gray-400`}></i>
                      </button>
                      {!isCollapsed && renderSectionTable(key, items)}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
          </>
        )}

        {/* 일정 등록 모달 */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                <h2 className="text-xl font-bold">일정 등록</h2>
                <button onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-gray-600 cursor-pointer"><i className="ri-close-line text-2xl"></i></button>
              </div>
              <form onSubmit={handleCreate} className="p-6 space-y-4">
                <div className="flex items-end gap-4">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">카테고리 *</label>
                    <select value={form.scheduleCategory} onChange={(e) => {
                      const cat = e.target.value;
                      const allDay = ALL_DAY_CATEGORIES.includes(cat);
                      setForm({ ...form, scheduleCategory: cat, isAllDay: allDay });
                    }} required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                      {SCHEDULE_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                  </div>
                  <label className="flex items-center gap-1.5 cursor-pointer pb-2">
                    <input type="checkbox" checked={form.isAllDay} onChange={(e) => setForm({ ...form, isAllDay: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                    <span className="text-sm text-gray-600 whitespace-nowrap">종일</span>
                  </label>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">제목 *</label>
                  <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{form.isAllDay ? '날짜 *' : '시작일시 *'}</label>
                    <input type={form.isAllDay ? 'date' : 'datetime-local'} value={form.startDt} onChange={(e) => setForm({ ...form, startDt: e.target.value })} required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  {!form.isAllDay && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">종료일시</label>
                      <input type="datetime-local" value={form.endDt} onChange={(e) => setForm({ ...form, endDt: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">고객명 (미등록 시)</label>
                  <input type="text" value={form.customerName} onChange={(e) => setForm({ ...form, customerName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                {(form.section === 'STORE' || form.section === 'CONSULTATION') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">연락처</label>
                    <input type="tel" value={form.customerPhone} onChange={(e) => setForm({ ...form, customerPhone: e.target.value })}
                      placeholder="010-0000-0000"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                )}
                {form.section === 'CONSULTATION' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">업체명</label>
                      <input type="text" value={form.partnerVendorName} onChange={(e) => setForm({ ...form, partnerVendorName: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">업체담당</label>
                      <input type="text" value={form.partnerStaffName} onChange={(e) => setForm({ ...form, partnerStaffName: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                  </>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">메모</label>
                  <textarea value={form.memo} onChange={(e) => setForm({ ...form, memo: e.target.value })} rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
                </div>
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <button type="button" onClick={() => setShowAddModal(false)} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer">취소</button>
                  <button type="submit" className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer">등록</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* 일정 상세/수정 모달 */}
        {showDetailModal && selectedSchedule && (() => {
          const s = selectedSchedule;
          const [isEditMode, setIsEditMode] = [s._editing || false, (v: boolean) => setSelectedSchedule({ ...s, _editing: v })];
          const updateField = (field: string, value: any) => setSelectedSchedule({ ...selectedSchedule, [field]: value });
          const toLocalDt = (v: string) => v ? v.replace(/\.000Z$/, '').slice(0, 16) : '';
          const fmtDt = (dt: string) => { if (!dt) return '-'; try { return new Date(dt).toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' }); } catch { return dt; } };

          return (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
                <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                  <h2 className="text-lg font-bold">{isEditMode ? '일정 수정' : '일정 상세'}</h2>
                  <button onClick={() => setShowDetailModal(false)} className="text-gray-400 hover:text-gray-600 cursor-pointer"><i className="ri-close-line text-2xl"></i></button>
                </div>
                <div className="p-6 space-y-4">
                  {isEditMode ? (
                    <>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">카테고리</label>
                        <select value={s.scheduleCategory} onChange={(e) => updateField('scheduleCategory', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                          {SCHEDULE_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">제목</label>
                        <input type="text" value={s.title} onChange={(e) => updateField('title', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">시작일시</label>
                          <input type="datetime-local" value={toLocalDt(s.startDt)} onChange={(e) => updateField('startDt', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">종료일시</label>
                          <input type="datetime-local" value={toLocalDt(s.endDt || '')} onChange={(e) => updateField('endDt', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">메모</label>
                        <textarea value={s.memo || ''} onChange={(e) => updateField('memo', e.target.value)} rows={2}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center gap-3">
                        <div className={`w-4 h-4 rounded-full ${getCategoryDotColor(s.scheduleCategory)}`}></div>
                        <span className="text-sm font-medium">{getCategoryLabel(s.scheduleCategory)}</span>
                      </div>
                      <h3 className="text-xl font-bold text-gray-900">{s.title}</h3>
                      <div className="text-sm text-gray-600">
                        <i className="ri-time-line mr-2"></i>{fmtDt(s.startDt)}{s.endDt ? ` ~ ${fmtDt(s.endDt)}` : ''}
                      </div>
                      {s.room && (
                        <div className="text-sm text-gray-600 flex items-center gap-2">
                          <i className="ri-store-2-line"></i>
                          <span>{s.room.name}</span>
                        </div>
                      )}
                      {(s.suitCustomer || s.customerName) && (
                        <div className="text-sm text-gray-600 flex items-center gap-2">
                          <i className="ri-user-line"></i>
                          {s.suitCustomer ? (
                            <button onClick={() => { setShowDetailModal(false); navigate(`/suit-shop/customer-detail?id=${s.suitCustomer.suitCustomerIdx}&tab=${s.scheduleCategory === 'PAYMENT' ? 'PAYMENTS' : 'OVERVIEW'}`); }}
                              className="text-blue-600 hover:text-blue-800 cursor-pointer">{s.suitCustomer.name}</button>
                          ) : <span>{s.customerName}</span>}
                        </div>
                      )}
                      {s.suitCustomer && (
                        <div className="flex gap-2 mt-1">
                          <button onClick={() => { setShowDetailModal(false); navigate(`/suit-shop/customer-detail?id=${s.suitCustomer.suitCustomerIdx}`); }}
                            className="text-xs px-2.5 py-1 bg-blue-50 text-blue-600 rounded-lg cursor-pointer hover:bg-blue-100 flex items-center gap-1">
                            <i className="ri-user-line text-[10px]"></i> 고객상세
                          </button>
                          {s.scheduleCategory === 'PAYMENT' && (
                            <button onClick={() => { setShowDetailModal(false); navigate(`/suit-shop/customer-detail?id=${s.suitCustomer.suitCustomerIdx}&tab=PAYMENTS`); }}
                              className="text-xs px-2.5 py-1 bg-amber-50 text-amber-700 rounded-lg cursor-pointer hover:bg-amber-100 flex items-center gap-1">
                              <i className="ri-money-dollar-circle-line text-[10px]"></i> 결제정보
                            </button>
                          )}
                        </div>
                      )}
                      {s.memo && <div className="bg-gray-50 p-3 rounded-lg text-sm text-gray-700">{s.memo}</div>}
                    </>
                  )}
                </div>
                <div className="px-6 py-4 border-t border-gray-100 flex justify-between">
                  <button onClick={async () => {
                    if (!confirm('이 일정을 삭제하시겠습니까?')) return;
                    try {
                      await scheduleApi.delete(s.suitScheduleIdx);
                      setShowDetailModal(false);
                      fetchCalendarSchedules();
                      fetchDailySchedules();
                    } catch (err: any) { alert(`삭제 실패: ${err.message}`); }
                  }} className="text-red-500 hover:text-red-700 text-sm cursor-pointer flex items-center gap-1">
                    <i className="ri-delete-bin-line"></i> 삭제
                  </button>
                  <div className="flex gap-2">
                    {isEditMode ? (
                      <>
                        <button onClick={() => setSelectedSchedule({ ...s, _editing: false })} className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 cursor-pointer">취소</button>
                        <button onClick={async () => {
                          try {
                            await scheduleApi.update(s.suitScheduleIdx, {
                              scheduleCategory: s.scheduleCategory,
                              title: s.title,
                              startDt: s.startDt,
                              endDt: s.endDt || undefined,
                              memo: s.memo,
                            });
                            setShowDetailModal(false);
                            fetchCalendarSchedules();
                            fetchDailySchedules();
                          } catch (err: any) { alert(`수정 실패: ${err.message}`); }
                        }} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 cursor-pointer">저장</button>
                      </>
                    ) : (
                      <button onClick={() => setSelectedSchedule({ ...s, _editing: true })} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 cursor-pointer flex items-center gap-1">
                        <i className="ri-edit-line"></i> 수정
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
}
